import "highcharts/modules/drilldown";

import { Card, Col, Row, Spin, Tag } from "antd"
import { useGetDashboardExctuiveSummaryData } from "features/dashboard/use-cases/get-exctuive-summary";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useEffect, useMemo, useState } from "react";
import { Fragment } from "react/jsx-runtime"
export const BoothTragetperMonth = ({ dateRange, selectedYear, selectedCategory, selectedEvent, chartRef }: any) => {
  const [selectedMonths, setSelectedMonths] = useState<string[]>([]);

  const monthShortNames = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sept", "Oct", "Nov", "Dec",
  ];
  const monthFullNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  const barColors = [
    "#E55050", "#008048", "#35B1E8", "#FFA51F",
    "#FEBEC8", "#4C5167", "#008044", "#35B1E8",
  ];

  // ✅ Build payload for API
  const monthListPayload = useMemo(() => {
    return selectedMonths
      .map((shortMonth) => monthShortNames.indexOf(shortMonth) + 1)
      .join(",");
  }, [selectedMonths]);

  // ✅ Fetch data
  const { data: expoboothGraph, isFetching: expoboothisFetching } =
    useGetDashboardExctuiveSummaryData({
      Flag: "expobooth",
      FromDate: dateRange?.[0]?.format("YYYY-MM-DD") || "",
      ToDate: dateRange?.[1]?.format("YYYY-MM-DD") || "",
      monthlist: monthListPayload || undefined,
      Year: selectedYear,
      CategoryId: selectedCategory,
      EventId: selectedEvent,
    } as any);

  // ✅ Monthly series from GraphData
  const monthlyDataFromApi =
    expoboothGraph?.GraphData?.map((item: any) => ({
      name: monthShortNames[item.GMonth - 1],
      y: item.TotalCount,
      revenue: item?.TotalRevenue,
      drilldown: monthFullNames[item.GMonth - 1], // <-- important
    })) || [];

  // ✅ Drilldown series from DrilldownData
  const drilldownSeriesFromApi =
    expoboothGraph?.DrilldownData?.length > 0
      ? Object.values(
        expoboothGraph.DrilldownData.reduce((acc: any, day: any) => {
          const monthFull = monthFullNames[day.GMonth - 1];
          if (!acc[monthFull]) {
            acc[monthFull] = {
              id: monthFull,
              name: `Daily Sales - ${monthFull}`,
              type: "column",
              colors: barColors,
              data: [],
            };
          }
         acc[monthFull].data.push({
            name: `${day.GDay} ${monthShortNames[day.GMonth - 1]}`,
            y: day.TotalCount,          // ✅ use count for the column height
            revenue: day.TotalRevenue,  // ✅ custom property for tooltip
          });

          return acc;
        }, {})
      )
      : [];

  // ✅ Base series (monthly or filtered months)
  const baseSeriesData =
    selectedMonths.length === 0
      ? monthlyDataFromApi
      : selectedMonths.map((shortMonth) => {
        const monthIndex = monthShortNames.indexOf(shortMonth);
        const total =
          expoboothGraph?.DrilldownData?.filter(
            (d: any) => d.GMonth - 1 === monthIndex
          ).reduce((sum: number, d: any) => sum + d.TotalRevenue, 0) || 0;

        return {
          name: shortMonth,
          y: total,
          drilldown: monthFullNames[monthIndex], // ensure drilldown works for filtered
        };
      });

  // ✅ Total revenue for subtitle
  const totalRevenue =
    expoboothGraph?.GraphData?.reduce(
      (sum: number, item: any) => sum + (item.TotalRevenue || 0),
      0
    ) || 0;

  // ✅ Chart options
  const chartOptions: Highcharts.Options = {
    chart: { type: "column" },
    title: {
      text:
        selectedMonths.length === 0
          ? "Booth Sales per Month (Registration)"
          : `Combined Sales for: ${selectedMonths.join(", ")}`,
    },
    lang: { noData: "No data found" },
    subtitle: {
      text: `Total Revenue: KWD ${totalRevenue.toLocaleString(undefined, {
        minimumFractionDigits: 2,
      })}`,
      style: {
        fontSize: "9px",
        fontWeight: "bold",
        color: "#333",
      },
    },
    credits: { enabled: false },
    xAxis: { type: "category" },
    yAxis: { title: { text: "Sales (KWD)" } },
    legend: { enabled: false },
    colors: barColors,
    plotOptions: {
      series: {
        borderWidth: 0,
        colorByPoint: true,
        dataLabels: {
          enabled: true,
          format: "{point.y:.0f}", // ✅ only top value,
          style: {
            color: "black",
            fontSize: "11px",
            fontWeight: "bold",
          },
        },
      },
    } as any,
   tooltip: {
      headerFormat: '<span style="font-size:11px">{series.name}</span><br/>',
      pointFormatter: function (this: any) {
        return `
      <span style="color:${this.color}">${this.name}</span><br/>
      <b>Total Count:</b> ${this.y}<br/>
      <b>Total Revenue:</b> KWD ${(this.revenue || 0)}
    `;
      }
    },
    series: [
      {
        name:
          selectedMonths.length === 0
            ? "Monthly Sales"
            : `Total Sales (${selectedMonths.length} month${selectedMonths.length > 1 ? "s" : ""
            })`,
        type: "column",
        data: baseSeriesData,
      },
    ],
    drilldown: {
      series: drilldownSeriesFromApi,
      activeAxisLabelStyle: { textDecoration: "none", color: "black" },
      activeDataLabelStyle: { textDecoration: "none", color: "black" },
    } as any,
  };

  // ✅ Handle chart loading state
  useEffect(() => {
    const chart = chartRef?.current?.chart;
    if (chart) {
      if (expoboothisFetching) {
        chart.showLoading("Loading data...");
      } else {
        chart.hideLoading();
      }
    }
  }, [expoboothisFetching]);

  // ✅ Month filter tags
  const monthlyData = monthShortNames.map((m) => ({ month: m }));

  const handleMonthToggle = (month: string) => {
    setSelectedMonths((prev) =>
      prev.includes(month) ? prev.filter((m) => m !== month) : [...prev, month]
    );
  };

  return (
    <Fragment>
      <Card>
        <Row gutter={[10, 10]} className="month_tabs">
          {monthlyData.map((month) => (
            <Col key={month.month}>
              <Tag
                color={selectedMonths.includes(month.month) ? "yellow" : "default"}
                style={{ cursor: "pointer" }}
                onClick={() => handleMonthToggle(month.month)}
              >
                {month.month}
              </Tag>
            </Col>
          ))}
        </Row>
        <Spin spinning={expoboothisFetching}>
          <HighchartsReact
            ref={chartRef}
            highcharts={Highcharts}
            options={chartOptions}
          />
        </Spin>
      </Card>
    </Fragment>
  );
}